/*
 * Display of to-dos created by the early warning ("Frühwarnung bei Abwesenheiten", workflow-manager).
 * New to-dos carry a structured `source` (attendance, course, mail status). Older ones only have the generated
 * description; it is parsed only if it has exactly the generated format, otherwise the text is shown as before.
 */
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {AbsenceEarlyWarningToDoSource, ToDoItemSourceTypes} from '@/documentTypes/to-do-manager/ToDoItemSource';
import {AbsenceEarlyWarningRecentItem} from '@/documentTypes/workflow-manager/AbsenceEarlyWarningRecord.document';
import {courseLine, PresenceDot, presenceDots, Tone} from '@/components/earlyWarning/earlyWarning.helper';
import {dotsFromLetters, earlyWarningMailText} from '@/components/timeline/earlyWarningLog.helper';

export type EarlyWarningToDoInfo = {
    /** Attendance dots, oldest → newest */
    dots: PresenceDot[];
    /** "Gitarre KS012 · Di 14:00 · Adolf-Kußmaul-Schule, Graben-Neudorf" */
    course: string;
    /** Course id for the link to the course page (only structured to-dos) */
    courseId?: string;
    mail?: { text: string; tone: Tone | string; icon: string; title?: string };
    ruleName?: string;
    /** Text that is not covered by the structured display (hand-edited description, unknown lines) */
    extraText?: string;
};

type ToDoLike = {
    entityType?: string;
    description?: string;
    source?: { type?: string } | null;
};

export const DOTS_LEGEND = 'A = anwesend · E = entschuldigt · U = unentschuldigt';

const LESSONS_LINE = /^Letzte Stunden \((neueste zuerst|älteste → neueste)\):\s*([AEU](?:\s+[AEU])*)\s*(?:\(A = anwesend.*\))?\s*$/;
const RULE_LINE = /^Regel:\s*(.+)$/;
const MAIL_LINE = /^(Eltern-Mail|Mail an|Keine Mail)\b/;

const legacyMailMeta = (line: string): EarlyWarningToDoInfo['mail'] => {
    if (/fehlgeschlagen/.test(line)) {
        return {text: line, tone: 'bad', icon: 'mdi-email-alert-outline'};
    }
    if (/ausgeschaltet/.test(line)) {
        return {text: line, tone: 'muted', icon: 'mdi-email-off-outline'};
    }
    if (/nicht verschickt|^Keine Mail/.test(line)) {
        return {text: line, tone: 'warn', icon: 'mdi-email-off-outline'};
    }
    return {text: line, tone: 'good', icon: 'mdi-email-check-outline'};
};

/** Older to-dos: only the generated description (course line, lessons line, mail line, rule line) */
const fromDescription = (description: string): EarlyWarningToDoInfo | null => {
    const lines = description.split('\n').map((line) => line.trim()).filter(Boolean);
    const lessonsIndex = lines.findIndex((line) => LESSONS_LINE.test(line));
    if (lessonsIndex < 0) {
        return null;
    }
    const [, order, letterText] = lines[lessonsIndex].match(LESSONS_LINE) as RegExpMatchArray;
    const letters = letterText.split(/\s+/);
    // dotsFromLetters expects the newest first
    const dots = dotsFromLetters(order === 'neueste zuerst' ? letters : letters.slice().reverse());
    const info: EarlyWarningToDoInfo = {dots, course: lessonsIndex > 0 ? lines[0] : ''};
    const rest: string[] = [];
    lines.forEach((line, index) => {
        if (index === lessonsIndex || (index === 0 && lessonsIndex > 0)) {
            return;
        }
        const rule = line.match(RULE_LINE);
        if (rule && !info.ruleName) {
            info.ruleName = rule[1];
        } else if (MAIL_LINE.test(line) && !info.mail) {
            info.mail = legacyMailMeta(line);
        } else {
            rest.push(line);
        }
    });
    info.extraText = rest.join('\n') || undefined;
    return info;
};

const fromSource = (source: AbsenceEarlyWarningToDoSource, description?: string): EarlyWarningToDoInfo => {
    const lessons = (source.lessons || []).map((l) => ({state: Number(l.state), instance: l.instance})) as unknown as AbsenceEarlyWarningRecentItem[];
    const course = source.course || {};
    const mail = source.mail?.status ? earlyWarningMailText({mail: source.mail}) : undefined;
    return {
        dots: presenceDots(lessons),
        course: courseLine({
            subjectName: course.subject,
            courseCustomId: course.customId,
            dayOfWeek: course.dayOfWeek,
            startTime: course.startTime,
            institution: {name: course.institutionName, city: course.city},
        }),
        courseId: course.course ? String(course.course) : undefined,
        mail: mail && mail.text ? {...mail, title: source.mail?.recipient} : undefined,
        ruleName: source.ruleName,
        extraText: source.descriptionEdited && description ? description : undefined,
    };
};

/** Display data of an early-warning to-do; null for every other to-do (shows its description as before) */
export const earlyWarningToDoInfo = (item: ToDoLike | null | undefined): EarlyWarningToDoInfo | null => {
    if (!item || item.entityType !== CrudEntityTypes.STUDENT) {
        return null;
    }
    if (item.source && item.source.type === ToDoItemSourceTypes.ABSENCE_EARLY_WARNING) {
        return fromSource(item.source as AbsenceEarlyWarningToDoSource, item.description);
    }
    return item.description ? fromDescription(item.description) : null;
};
